import { Link, useLocation, useNavigate } from "react-router";
import { useState } from "react";
import {
  LayoutDashboard,
  Store,
  Trash2,
  DollarSign,
  Users,
  UserMinus,
  Flag,
  Settings,
  Tags,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Download,
  Megaphone,
  UserX,
  CalendarClock,
  PartyPopper,
  Star,
  KeyRound,
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { storeDetailPath, TAB_EVENTS } from "@/lib/utils/storeTabs";

type NavItem = {
  to: string;
  icon: React.ElementType;
  label: string;
  // Hidden outside the production Firebase project (e.g. a shortcut to a store that only exists in prod).
  prodOnly?: boolean;
};

// True when running against the production Firebase project (see disca-admin-react/CLAUDE.md's Environments table).
const IS_PROD = import.meta.env.VITE_FIREBASE_PROJECT_ID === "disco-fb-pro";

const NAV_ITEMS: { section: string; items: NavItem[] }[] = [
  {
    section: "Overview",
    items: [{ to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" }],
  },
  {
    section: "Shortcuts",
    items: [
      {
        to: storeDetailPath("642e5bba-117f-4d14-9261-d1f5a0145bbb", TAB_EVENTS),
        icon: PartyPopper,
        label: "Aura Nightclub Events",
        prodOnly: true,
      },
    ],
  },
  {
    section: "Stores",
    items: [
      { to: "/stores", icon: Store, label: "Stores" },
      { to: "/stores/removal-requests", icon: Trash2, label: "Removal Requests" },
      { to: "/stores/price-limit-bypass", icon: DollarSign, label: "Bypass Requests" },
      {
        to: "/stores/appointments-enabled-requests",
        icon: CalendarClock,
        label: "Appointment Requests",
      },
    ],
  },
  {
    section: "Users",
    items: [
      { to: "/users", icon: Users, label: "Users" },
      { to: "/users/deletion-requests", icon: UserMinus, label: "Deletion Requests" },
    ],
  },
  {
    section: "Guestlist Report",
    items: [
      { to: "/guestlist-report?is_promoter=true", icon: Megaphone, label: "Promoters" },
      { to: "/guestlist-report?is_promoter=false", icon: UserX, label: "Non-Promoters" },
    ],
  },
  {
    section: "Platform",
    items: [
      { to: "/reports", icon: Flag, label: "Reports" },
      { to: "/default-menu-categories", icon: Tags, label: "Default Menu Categories" },
      { to: "/featured-rewards", icon: Star, label: "Featured Rewards" },
      { to: "/staff-invitations", icon: KeyRound, label: "Staff Invitations" },
      { to: "/settings", icon: Settings, label: "Settings" },
    ],
  },
  {
    section: "Export",
    items: [{ to: "/export/events", icon: Download, label: "Events" }],
  },
];

// Named sub-routes that should NOT activate the parent list item.
const STORE_SUBROUTES = [
  "/stores/removal-requests",
  "/stores/price-limit-bypass",
  "/stores/appointments-enabled-requests",
];
const USER_SUBROUTES = ["/users/deletion-requests"];

/**
 * NavLink's `end` prop only supports exact vs prefix — there's no built-in
 * "prefix but exclude named sub-routes". So we compute active state ourselves:
 * list routes (/stores, /users, /reports) are active on their detail pages
 * (/stores/:id etc.) but NOT on their own named sub-routes.
 */
function isNavActive(to: string, pathname: string, search: string): boolean {
  if (to === "/stores") {
    return (
      pathname === "/stores" ||
      (pathname.startsWith("/stores/") && !STORE_SUBROUTES.some((s) => pathname.startsWith(s)))
    );
  }
  if (to === "/users") {
    return (
      pathname === "/users" ||
      (pathname.startsWith("/users/") && !USER_SUBROUTES.some((s) => pathname.startsWith(s)))
    );
  }
  if (to === "/reports") {
    return pathname === "/reports" || pathname.startsWith("/reports/");
  }
  if (to.startsWith("/guestlist-report")) {
    const [toPath, toQuery] = to.split("?");
    const toIsPromoter = new URLSearchParams(toQuery ?? "").get("is_promoter") !== "false";
    const currentIsPromoter = new URLSearchParams(search).get("is_promoter") !== "false";
    return pathname === toPath && toIsPromoter === currentIsPromoter;
  }
  if (to.startsWith("/stores/") && to.includes("?tab=")) {
    const [toPath, toQuery] = to.split("?");
    const toTab = new URLSearchParams(toQuery ?? "").get("tab");
    return pathname === toPath && new URLSearchParams(search).get("tab") === toTab;
  }
  return pathname === to;
}

type SidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
};

export function Sidebar({ collapsed, onToggle, mobileOpen }: SidebarProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut(auth);
      void navigate("/login", { replace: true });
    } finally {
      setSigningOut(false);
      setSignOutOpen(false);
    }
  }

  return (
    <>
      <aside
        className={cn(
          "flex h-full flex-col bg-inverse text-inverse-foreground transition-all duration-200",
          // Mobile: absolute overlay, slides in/out
          "max-lg:absolute max-lg:inset-y-0 max-lg:left-0 max-lg:z-50 max-lg:w-64",
          mobileOpen ? "max-lg:translate-x-0" : "max-lg:-translate-x-full",
          // Desktop: fixed in the flex row, width varies
          collapsed ? "lg:w-14" : "lg:w-56"
        )}
      >
        {/* Logo / header */}
        <div
          className={cn(
            "flex h-14 items-center border-b border-inverse-secondary px-3",
            collapsed ? "justify-center" : "justify-between"
          )}
        >
          {!collapsed && (
            <span className="text-sm font-bold tracking-widest text-inverse-foreground">
              DISCA<span className="text-primary"> Admin</span>
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex size-8 text-inverse-muted hover:bg-inverse-secondary hover:text-inverse-foreground"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="size-4" /> : <ChevronLeft className="size-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_ITEMS.map(({ section, items: allItems }) => {
            const items = allItems.filter((item) => !item.prodOnly || IS_PROD);
            if (items.length === 0) return null;
            return (
              <div key={section} className="mb-1">
                {!collapsed && (
                  <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-inverse-muted">
                    {section}
                  </p>
                )}
                {items.map(({ to, icon: Icon, label }) => {
                  const active = isNavActive(to, pathname, search);
                  return (
                    <Link
                      key={to}
                      to={to}
                      className={cn(
                        "flex items-center gap-2.5 px-3 py-2 text-sm transition-colors",
                        collapsed && "justify-center",
                        active
                          ? "bg-primary text-white"
                          : "text-inverse-subtle hover:bg-inverse-secondary hover:text-inverse-foreground"
                      )}
                      title={collapsed ? label : undefined}
                    >
                      <Icon className="size-4 shrink-0" />
                      {!collapsed && <span>{label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Sign out */}
        <div className="border-t border-inverse-secondary p-2">
          <Button
            variant="ghost"
            className={cn(
              "w-full text-inverse-muted hover:bg-inverse-secondary hover:text-red-400",
              collapsed ? "justify-center px-0" : "justify-start gap-2.5"
            )}
            onClick={() => setSignOutOpen(true)}
          >
            <LogOut className="size-4 shrink-0" />
            {!collapsed && <span className="text-sm">Sign out</span>}
          </Button>
        </div>
      </aside>

      <ConfirmDialog
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        title="Sign out"
        description="Are you sure you want to sign out?"
        confirmLabel="Sign out"
        onConfirm={handleSignOut}
        isPending={signingOut}
      />
    </>
  );
}
